/**
 * KimaniOnboarding — first-visit guided tour for the Kimani AI workspace.
 *
 * Shows a step-through spotlight overlay that highlights key capabilities:
 *   1. Welcome / overview
 *   2. Language toggle (EN / SW / Sheng)
 *   3. Output channel (Text / Voice / Graph)
 *   4. Depth selector (Simple / Technical / Enhanced)
 *   5. Signals inbox
 *   6. Chat input (CTA to start)
 *
 * Persists completion to localStorage (`md_kimani_onboarded`).
 * Only shows for users who haven't completed or dismissed it.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
    Sparkles, Languages, MessageSquareText, Volume2,
    BarChart3, Bell, Zap, ArrowRight, X, ChevronRight, ChevronLeft,
} from "lucide-react";

const STORAGE_KEY = "md_kimani_onboarded";

const STEPS = [
    {
        id: "welcome",
        target: null,
        title: "Welcome to Kimani",
        description: "Your free AI trading copilot. Kimani is portfolio-aware, multilingual, and streams live answers about your positions, market mechanics, and strategy.",
        icon: Sparkles,
        position: "center",
    },
    {
        id: "language",
        target: '[data-testid="assistant-lang-en"]',
        title: "Speak your language",
        description: "Switch between English, Kiswahili, and Sheng. Kimani adapts its tone and vocabulary to match.",
        icon: Languages,
        position: "below",
    },
    {
        id: "output",
        target: '[data-testid="assistant-channel-text"]',
        title: "Choose your output",
        description: "Text for reading, Voice for hands-free listening, or Graph to generate inline charts and visual analysis.",
        icon: MessageSquareText,
        position: "below",
    },
    {
        id: "depth",
        target: '[data-testid="assistant-depth-technical"]',
        title: "Set your depth",
        description: "Simple for quick answers, Technical for detailed market analysis, Enhanced for institutional-grade breakdown.",
        icon: Zap,
        position: "below",
    },
    {
        id: "signals",
        target: '[data-testid="assistant-signals-toggle"]',
        title: "Proactive signals",
        description: "Kimani watches your watchlist and pushes trade signals, news deltas, and risk alerts as they fire.",
        icon: Bell,
        position: "below-left",
    },
    {
        id: "chat",
        target: '[data-testid="assistant-input"]',
        title: "Start asking",
        description: "Try the suggestion chips above, or ask anything about your portfolio, a stock, or a concept. Kimani is ready.",
        icon: ArrowRight,
        position: "above",
    },
];

export default function KimaniOnboarding({ onComplete }) {
    const [step, setStep] = useState(0);
    const [targetRect, setTargetRect] = useState(null);
    const overlayRef = useRef(null);

    const current = STEPS[step];
    const isLast = step === STEPS.length - 1;

    const measureTarget = useCallback(() => {
        if (!current?.target) {
            setTargetRect(null);
            return;
        }
        const el = document.querySelector(current.target);
        if (el) {
            const r = el.getBoundingClientRect();
            setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        } else {
            setTargetRect(null);
        }
    }, [current]);

    useEffect(() => {
        measureTarget();
        window.addEventListener("resize", measureTarget);
        window.addEventListener("scroll", measureTarget, true);
        return () => {
            window.removeEventListener("resize", measureTarget);
            window.removeEventListener("scroll", measureTarget, true);
        };
    }, [measureTarget]);

    const finish = useCallback(() => {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
        onComplete?.();
    }, [onComplete]);

    const next = () => {
        if (isLast) { finish(); return; }
        setStep((s) => s + 1);
    };

    const prev = () => setStep((s) => Math.max(0, s - 1));

    const Icon = current.icon;

    // Tooltip position calculation
    const getTooltipStyle = () => {
        if (current.position === "center" || !targetRect) {
            return {
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
            };
        }

        const pad = 12;
        const style = { position: "fixed" };

        if (current.position === "below") {
            style.top = targetRect.top + targetRect.height + pad;
            style.left = Math.max(16, targetRect.left + targetRect.width / 2 - 160);
        } else if (current.position === "below-left") {
            style.top = targetRect.top + targetRect.height + pad;
            style.left = Math.max(16, targetRect.left - 200);
        } else if (current.position === "above") {
            style.bottom = window.innerHeight - targetRect.top + pad;
            style.left = Math.max(16, targetRect.left + targetRect.width / 2 - 160);
        }

        return style;
    };

    return (
        <div
            ref={overlayRef}
            className="fixed inset-0 z-[100]"
            data-testid="kimani-onboarding-overlay"
        >
            {/* Dark overlay with spotlight cutout */}
            <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
                <defs>
                    <mask id="spotlight-mask">
                        <rect width="100%" height="100%" fill="white" />
                        {targetRect && (
                            <rect
                                x={targetRect.left - 6}
                                y={targetRect.top - 6}
                                width={targetRect.width + 12}
                                height={targetRect.height + 12}
                                rx="8"
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.65)"
                    mask="url(#spotlight-mask)"
                />
            </svg>

            {/* Spotlight ring around target */}
            {targetRect && (
                <div
                    className="absolute rounded-lg border-2 border-primary shadow-[0_0_0_4px_rgba(146,37,41,0.2)] transition-all duration-300"
                    style={{
                        top: targetRect.top - 6,
                        left: targetRect.left - 6,
                        width: targetRect.width + 12,
                        height: targetRect.height + 12,
                        pointerEvents: "none",
                    }}
                />
            )}

            {/* Tooltip card */}
            <div
                className="w-[320px] rounded-xl border border-border shadow-2xl p-5 bg-card text-card-foreground animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
                style={{
                    ...getTooltipStyle(),
                    zIndex: 101,
                }}
                data-testid="kimani-onboarding-tooltip"
            >
                {/* Skip button */}
                <button
                    onClick={finish}
                    className="absolute top-3 right-3 p-1 rounded-md hover:bg-secondary/60 transition-colors text-muted-foreground hover:text-foreground"
                    data-testid="kimani-onboarding-skip"
                    aria-label="Skip tour"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Icon + Title */}
                <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#922529" }}>
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-sm font-bold">{current.title}</h3>
                </div>

                {/* Description */}
                <p className="text-xs text-muted-foreground leading-relaxed mb-4">{current.description}</p>

                {/* Progress + Navigation */}
                <div className="flex items-center justify-between">
                    {/* Step dots */}
                    <div className="flex items-center gap-1">
                        {STEPS.map((_, i) => (
                            <div
                                key={i}
                                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                    i === step ? "bg-primary" : i < step ? "bg-primary/40" : "bg-border"
                                }`}
                            />
                        ))}
                        <span className="text-[10px] text-muted-foreground ml-1.5">
                            {step + 1}/{STEPS.length}
                        </span>
                    </div>

                    {/* Buttons */}
                    <div className="flex items-center gap-1.5">
                        {step > 0 && (
                            <button
                                onClick={prev}
                                className="h-7 px-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors inline-flex items-center gap-1"
                                data-testid="kimani-onboarding-prev"
                            >
                                <ChevronLeft className="w-3 h-3" /> Back
                            </button>
                        )}
                        <button
                            onClick={next}
                            className="h-7 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                            style={{ background: "#922529", color: "#fff" }}
                            data-testid="kimani-onboarding-next"
                        >
                            {isLast ? "Start chatting" : "Next"} <ChevronRight className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Check if onboarding has been completed */
export function hasCompletedKimaniOnboarding() {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
}
